import Vue from 'vue'
import {dispatch, make} from 'vuex-pathify'
import Constants from '@/utilities/constants'
import {ptracGet, ptracPatchEntity, ptracPostEntity, ptracPutEntity} from '@/utilities/ptracApi'
import {CREATED, OK} from 'http-status-codes'
import {isEmptyString} from "@/utilities/utils";

const initialState = {
    poProjectData: {},
    matchingProject: {},
    possibleMatchingProjects: [],
    projectSet: false,
    surveyOrInspectionSelected: false,
    formattedPoStreet: {
        number: null,
        name: null
    },
    ptracProject: {
        id: null,
        name: null,
        projectType: null,
        notesByDepartment: {},
        departments: [],
        active: true,
        createdBy: null,
        createdAt: null,
        updatedBy: null,
        updatedAt: null,
        address: {
            city: null,
            county: null,
            state: Constants.ATS.DEFAULT.STATE,
            zip: null,
            streetNumber: null,
            suiteNumber: null,
            buildingNumber: null,
            floorNumber: null,
            streetName: null,
            jurisdiction: null,
            lotNumber: null,
            blockNumber: null,
            phase: null,
            section: null,
            legalDescription: null,
            legalSubdivisionId: null,
        }
    }
}

const state = Vue.util.extend({}, JSON.parse(JSON.stringify(initialState)))

const getters = make.getters(state)
const mutations = make.mutations(state)

mutations.reset = () => {
    state.poProjectData = {}
    state.matchingProject = {}
    state.possibleMatchingProjects = []
    state.projectSet = false
    state.surveyOrInspectionSelected = false
    mutations.resetPtracProject()
}

mutations.addDepartment = (state, departmentId) => {
    if (!state.ptracProject.departments.includes(departmentId)) {
        state.ptracProject.departments.push(departmentId)
    }
}

mutations.resetPtracProject = () => {
    state.ptracProject.id = null
    state.ptracProject.name = null
    state.ptracProject.projectType = null
    state.ptracProject.notesByDepartment = {}
    state.ptracProject.departments = []
    state.ptracProject.active = true
    state.ptracProject.createdBy = null
    state.ptracProject.createdAt = null
    state.ptracProject.updatedBy = null
    state.ptracProject.updatedAt = null
    for (let f in state.ptracProject.address) {
        state.ptracProject.address[f] = null
    }
    state.ptracProject.address.state = Constants.ATS.DEFAULT.STATE
}

mutations.updatePtracProjectFromIncomingProject = (state, incomingProject) => {
    for (let key in state.ptracProject) {
        if (key === 'address') {
            for (let f in state.ptracProject.address) {
                Object.assign(state.ptracProject.address, {[f]: incomingProject.address[f]})
            }
        } else {
            if (incomingProject.hasOwnProperty(key)) {
                Object.assign(state.ptracProject, {[key]: incomingProject[key]})
            }
        }
    }
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions: {
        async getPoProjectData({ state, rootState }) {
            const jobObject = rootState.supplyPro.purchaseOrder.purchaseOrder.header.job
            const projectKeys = [
                Constants.SUPPLYPRO.PROJECT_FIELDS.STREET,
                'streetSupplement', // Not being used yet
                Constants.SUPPLYPRO.PROJECT_FIELDS.CITY,
                Constants.SUPPLYPRO.PROJECT_FIELDS.STATE_CODE,
                Constants.SUPPLYPRO.PROJECT_FIELDS.POSTAL_CODE,
                Constants.SUPPLYPRO.PROJECT_FIELDS.SUBDIVISION,
                Constants.SUPPLYPRO.PROJECT_FIELDS.LOT,
                Constants.SUPPLYPRO.PROJECT_FIELDS.BLOCK,
                Constants.SUPPLYPRO.PROJECT_FIELDS.PHASE]

            for (const key in jobObject) {
                if (projectKeys.includes(key)) {
                    Object.assign(state.poProjectData, {[key]: jobObject[key]})
                    if (key === Constants.SUPPLYPRO.PROJECT_FIELDS.STREET) {
                        let streetName = ''
                        for (const subString of jobObject[key].split(' ')) {
                            if (!subString.match(/\d+/g)) {
                                streetName += subString + ' '
                            } else if (subString.match(/\d+/g)) {
                                state.formattedPoStreet.number = subString
                            }
                        }
                        const abbreviatedResponse = await ptracGet('supply-pro-purchase-orders/maybeAbbreviateStreetName?streetName=' + streetName)
                        if (OK === abbreviatedResponse.status) {
                            state.formattedPoStreet.name = abbreviatedResponse.data.abbreviated.trim()
                        }
                    }
                }
            }
        },
        async findMatchingProjects({ state, commit, dispatch, rootState}) {
            // First thing to do: call the API to update PO.matchingProjectId/ PO.possibleMatchingProjectIds, as things may have changed since PO was receieved
            let response = await ptracGet('supply-pro-purchase-orders/updateMatchingProjects?purchaseOrderId=' + rootState.supplyPro.purchaseOrder.purchaseOrder.id)
            if (OK === response.status) {
                commit('supplyPro/purchaseOrder/purchaseOrder', response.data, {root: true})
            }

            const matchingProjectId = rootState.supplyPro.purchaseOrder.purchaseOrder.matchingProjectId
            const possibleMatchingProjectIds = rootState.supplyPro.purchaseOrder.purchaseOrder.possibleMatchingProjectIds

            // After updating PO, check for matchingProjectId (confirmed match) and if found, load project
            if (matchingProjectId) {
                response = await dispatch('projects/fetchById', matchingProjectId, {root: true})
                if (OK === response.status && Object.keys(response.data).length > 0) {
                    state.matchingProject = response.data
                // if a project NOT_FOUND, presumably was deleted since the PO was receieved. Will need to make sure that project deletion also deletes associated metadata
                // once the metadata is gone, matchingProjectId/possibleMatchingProjectIds are updated every time the PO is loaded
                } else {
                    return response
                }
            }
            // If not, check for possibleMatchingProjectIds (matches by address) and if found, load projects
            if (possibleMatchingProjectIds && possibleMatchingProjectIds.length > 0) {
                response = await dispatch('projects/fetchByIds', possibleMatchingProjectIds, {root: true})
                if (OK === response.status && Object.keys(response.data).length > 0) {
                    state.possibleMatchingProjects = response.data
                } else {
                    return response
                }
            }
            return {'status': OK, 'data': "Matching project(s) found"}
        },
        async populateFieldsFromExistingProject({ state }, possibleMatchingProjectIndex = 0) {
            let project
            if (Object.keys(state.matchingProject).length > 0) {
                project = state.matchingProject
            } else if (state.possibleMatchingProjects.length > 0) {
                project = state.possibleMatchingProjects[possibleMatchingProjectIndex]
            }

            for (const key in state.ptracProject.address) { // Maybe refactor with recursive function
                if (project.address.hasOwnProperty(key)) {
                    state.ptracProject.address[key] = project.address[key]
                }
            }
            for (const key in state.ptracProject) {
                if (project.hasOwnProperty(key) && key !== 'address') {
                    state.ptracProject[key] = project[key]
                }
            }
        },
        async populateFieldsForCreateProject({ state, commit, rootState }) {
            commit('resetPtracProject')
            const ptracAddress = state.ptracProject.address
            const response = await dispatch('cities/fetchByName', state.poProjectData.city, {root: true})
            let cityData
            if (OK === response.status) {
                cityData = response.data
            } else {
                console.warn('Failed to fetch City data')
            }
            for (const key in state.poProjectData) {
                let val = state.poProjectData[key]
                if (!isEmptyString(val)) {
                    if (key === Constants.SUPPLYPRO.PROJECT_FIELDS.CITY && cityData) {
                        ptracAddress.city = cityData.name
                    }
                    if (key === Constants.SUPPLYPRO.PROJECT_FIELDS.STREET) {
                        if (state.formattedPoStreet.number) {
                            ptracAddress.streetNumber = state.formattedPoStreet.number
                        }
                        if (state.formattedPoStreet.name) {
                            const streetResponse = await dispatch('streets/fetchByName', state.formattedPoStreet.name, {root: true})
                            if (OK === streetResponse.status) {
                                ptracAddress.streetName = streetResponse.data.name
                            }
                        }
                    }
                    if (key === Constants.SUPPLYPRO.PROJECT_FIELDS.STATE_CODE) {
                        ptracAddress.state = val.substring(2)
                    }
                    if (key === Constants.SUPPLYPRO.PROJECT_FIELDS.POSTAL_CODE && cityData.zipCodes.includes(val)) {
                        ptracAddress.zip = val
                    }
                    if (key === Constants.SUPPLYPRO.PROJECT_FIELDS.BLOCK) {
                        ptracAddress.blockNumber = val
                    }
                    if (key === Constants.SUPPLYPRO.PROJECT_FIELDS.LOT) {
                        ptracAddress.lotNumber = val
                    }
                    if (key === Constants.SUPPLYPRO.PROJECT_FIELDS.PHASE) {
                        ptracAddress.phase = val
                    }
                }
            }
        },
        async createProject({state, commit, dispatch}) {
            const response = await ptracPostEntity('projects', state.ptracProject)
            if (CREATED !== response.status) {
                return response
            }
            commit('resetPtracProject')
            commit('updatePtracProjectFromIncomingProject', response.data)
            const confirmResponse = await dispatch('confirmProject')
            if (OK !== confirmResponse.status) {
                //if confirm works, need to delete project?
                return confirmResponse
            }
            return confirmResponse
        },
        async updateProject({state, commit, dispatch}) {
            const response = await ptracPutEntity(`projects/${state.ptracProject.id}`, state.ptracProject)
            if (OK !== response.status) {
                return response
            }
            commit('resetPtracProject')
            commit('updatePtracProjectFromIncomingProject', response.data)
            return response
        },
        async confirmProject({state, rootState}) {
            const purchaseOrderId = rootState.supplyPro.purchaseOrder.purchaseOrder.id
            const response = await ptracPatchEntity(`supply-pro-purchase-orders/confirmMatchingProject?purchaseOrderId=${purchaseOrderId}&projectId=${state.ptracProject.id}`)
            if (OK !== response.status) {
                return response
            }
            if (OK === response.status) {
                state.matchingProject = JSON.parse(JSON.stringify(state.ptracProject))
            }
            state.possibleMatchingProjects = []
            return response
        },
    }
}
